// Customer picker panel — compact card + opens command palette on search.
// Extracted from QuoteBuilder.jsx (Wave K refactor).
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Phone, User as UserIcon, UserPlus, Activity, Users, Copy as CopyIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { GBP } from "../_quoteHelpers";
import { T2Panel, T2Chip, T2TierChip, T2Stat, T2CommandPalette } from "../_t2";
import NewLeadInlineModal from "./NewLeadInlineModal";
import QuotesPeekModal from "./QuotesPeekModal";
import QuoteAddressPicker from "./QuoteAddressPicker";
import RecentOrdersPanel from "./RecentOrdersPanel";

// Hoisted so React doesn't re-mount it on every CustomerPanel render
// (silences no-unstable-nested-components — this used to be an inline
// arrow inside `emptyAction`).
function EmptyStateCreateLead({ query, onSeed }) {
  return (
    <button
      type="button"
      onClick={() => onSeed({ seed: query })}
      data-testid="palette-create-lead-btn"
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 10,
        padding: "12px 14px", borderRadius: 10, cursor: "pointer",
        background: "#0369a1",
        border: "1px solid #0ea5e9",
        color: "#ffffff",
        fontSize: 13, fontWeight: 600,
        boxShadow: "0 6px 18px rgba(14,165,233,0.35)",
        position: "relative", zIndex: 1,
      }}
    >
      <UserPlus className="w-4 h-4" style={{ color: "#ffffff" }} />
      <span style={{ flex: 1, textAlign: "left" }}>
        Create as new lead{query ? `: “${query}”` : ""}
      </span>
      <kbd className="t2-mono" style={{ fontSize: 10, color: "rgba(255,255,255,0.85)", background: "rgba(0,0,0,0.25)", padding: "2px 6px", borderRadius: 4 }}>New</kbd>
    </button>
  );
}

export default function CustomerPanel({ customer, billing, addrBook, onBillingChange, delivery, onDeliveryAddrChange, tierInfo, onPick, onClear, onLog, onOpen360 }) {
  const [addrTab, setAddrTab] = useState("invoice");
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [scope, setScope] = useState("all");           // all | deals | leads
  const [counts, setCounts] = useState({ all: 0, deals: 0, leads: 0 });
  const [lastQ, setLastQ] = useState("");
  const [newLeadDraft, setNewLeadDraft] = useState(null); // { seed: "..." } opens the mini form
  const [quotesPeek, setQuotesPeek] = useState(null);     // { email, name } opens the quotes mini-modal

  const query = async (q, scopeArg) => {
    const sc = scopeArg ?? scope;
    setLastQ(q || "");
    if (!q || q.length < 2) { setResults([]); setCounts({ all: 0, deals: 0, leads: 0 }); return; }
    // "all" omits the pipeline param; leads/deals map 1:1 to the backend value.
    const scopeParam = sc === "leads" ? "&pipeline=lead" : sc === "deals" ? "&pipeline=deal" : "";
    try {
      const { data } = await api.get(`/quotes/customer-search?q=${encodeURIComponent(q)}${scopeParam}&limit=20`);
      setResults((data.items || []).map((r, i) => ({ ...r, _key: r.email || r.personId || `r${i}` })));
      setCounts(data.counts || { all: 0, deals: 0, leads: 0 });
    } catch { setResults([]); }
  };

  const changeScope = (sc) => { setScope(sc); if (lastQ) query(lastQ, sc); };

  const has = !!customer?.email;

  const attachLead = async (form) => {
    // Create lead, then convert to Customer sub-doc + attach.
    const { data } = await api.post(`/leads`, form);
    // Fetch the newly created lead as a full person and shape it as customer.
    const p = data.person || {};
    const cust = {
      email:     p.Email,
      firstName: p.FirstName || form.first_name,
      lastName:  p.LastName || form.last_name || "",
      company:   p.Company || "",
      phone:     p.Phone || "",
      tier:      "",
      shortTier: "",
      ltv:       0,
      orders:    0,
      avgOrder:  null,
      lastOrder: null,
      // Wave M: link via `personId` (Mongo _id) + `PersonId` (Pipedrive
      // integer, null for Talmia-native leads).
      personId:  data.person_id,
      PersonId:  p.PersonId ?? null,
      isLead:    true,
    };
    onPick(cust);
    setNewLeadDraft(null);
    setPaletteOpen(false);
    toast.success(`Lead created: ${[cust.firstName, cust.lastName].filter(Boolean).join(" ")}`);
  };

  return (
    <>
      <T2Panel
        icon={UserIcon}
        title="Customer"
        testid="panel-customer"
        right={has ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {onLog && (
              <button
                type="button"
                className="t2-mono"
                onClick={() => onLog("activity")}
                data-testid="customer-log-activity"
                title="Log an activity or note for this customer"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "3px 8px", borderRadius: 6, cursor: "pointer",
                  fontSize: 10, fontWeight: 700,
                  background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.4)",
                  color: "#c4b5fd",
                }}
              >
                <Activity className="w-3 h-3" /> Log
              </button>
            )}
            <button className="t2-mono" style={{ color: "var(--t2-red-ink)", background: "none", border: "none", cursor: "pointer" }}
              onClick={onClear} data-testid="customer-clear">Clear</button>
          </span>
        ) : null}
      >
        {has ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
              {customer.isLead
                ? <T2Chip tone="sky">LEAD</T2Chip>
                : (customer.tier && <T2TierChip tier={customer.tier} />)
              }
              {!customer.isLead && tierInfo?.matched && (
                <span
                  data-testid="customer-tier-price-chip"
                  title={tierInfo.custom
                    ? `${tierInfo.tier} is a custom tier — set line prices manually`
                    : `Auto-applies ${tierInfo.disc}% off retail on every line`}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700,
                    fontFamily: "var(--t2-mono)",
                    background: tierInfo.custom ? "rgba(139,92,246,0.15)" : "rgba(255,104,32,0.14)",
                    border: `1px solid ${tierInfo.custom ? "rgba(139,92,246,0.4)" : "rgba(255,104,32,0.4)"}`,
                    color: tierInfo.custom ? "var(--t2-violet-ink)" : "var(--t2-orange-ink)",
                  }}
                >
                  {tierInfo.custom ? `${tierInfo.tier} · Custom` : `${tierInfo.tier} · −${tierInfo.disc}%`}
                </span>
              )}
              {customer.groupId != null && <T2Chip tone="violet">Grp #{customer.groupId}</T2Chip>}
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--t2-white)", lineHeight: 1.3 }}>
              {[customer.firstName, customer.lastName].filter(Boolean).join(" ") || customer.email}
            </div>
            <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-orange-ink)", wordBreak: "break-all", marginTop: 2 }}>{customer.email}</div>
            {customer.company && <div style={{ fontSize: 11, color: "var(--t2-grey2)", marginTop: 2 }}>{customer.company}</div>}
            {customer.phone && (
              <div className="t2-mono" style={{ fontSize: 12, color: "var(--t2-grey2)", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                <Phone className="w-3 h-3" /> <span data-testid="customer-phone">{customer.phone}</span>
                <button type="button" data-testid="copy-phone-btn" title="Copy number for the JustCall dialer"
                  onClick={async () => {
                    const digits = String(customer.phone).replace(/[^\d+]/g, "");
                    try { await navigator.clipboard.writeText(digits); toast.success(`Copied ${digits}`); }
                    catch { toast.error("Copy failed — select the number manually"); }
                  }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 7px", borderRadius: 5, cursor: "pointer", fontSize: 9.5, fontWeight: 800, letterSpacing: ".06em",
                    border: "1px solid var(--t2-border)", background: "transparent", color: "var(--t2-teal-ink)" }}>
                  <CopyIcon className="w-3 h-3" /> COPY
                </button>
              </div>
            )}
            <RecentOrdersPanel customer={customer} embedded />
            {/* INVOICE TO — auto-pulled from the customer record; prints on every quote. */}
            <div
              data-testid="customer-invoice-address"
              style={{
                marginTop: 12, padding: "10px 12px", borderRadius: 8,
                background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }} data-testid="address-tabs">
                {[["invoice", "Invoice to"], ["delivery", "Deliver to"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setAddrTab(k)} data-testid={`address-tab-${k}`} className="t2-mono"
                    style={{ flex: 1, fontSize: 9.5, letterSpacing: ".12em", textTransform: "uppercase", padding: "5px 6px", borderRadius: 6, cursor: "pointer",
                      border: `1px solid ${addrTab === k ? "var(--t2-orange)" : "var(--t2-border)"}`,
                      background: addrTab === k ? "color-mix(in srgb, var(--t2-orange) 14%, transparent)" : "transparent",
                      color: addrTab === k ? "var(--t2-orange-ink)" : "var(--t2-grey2)", fontWeight: 800 }}>
                    {l}
                  </button>
                ))}
              </div>
              {addrTab === "invoice" ? (
                <>
                  {billing?.company && <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--t2-white)", marginBottom: 4 }}>{billing.company}</div>}
                  <QuoteAddressPicker
                    kind="billing" book={addrBook} mode={billing?.mode} selectedId={billing?.addressId}
                    address={billing} fallback={addrBook?.data?.billing} onChange={onBillingChange} testid="quote-billing"
                  />
                  {billing?.vat && <div className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey3)", marginTop: 4 }}>VAT {billing.vat}</div>}
                </>
              ) : (
                <QuoteAddressPicker
                  kind="delivery" book={addrBook} mode={delivery?.addressSource === "manual" ? "custom" : (delivery?.addressSource || "invoice")}
                  selectedId={delivery?.addressId} address={delivery?.deliveryAddress || {}} fallback={billing} allowInvoice
                  onChange={onDeliveryAddrChange} testid="delivery-address"
                />
              )}
            </div>
            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: onOpen360 ? "1fr 1fr" : "1fr", gap: 8 }}>
              {onOpen360 && (
                <button onClick={onOpen360} className="t2-btn-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-teal-400/60 bg-teal-500/15 text-teal-200 hover:bg-teal-500/25 font-semibold" style={{ fontSize: 11.5, padding: "8px 10px" }} data-testid="customer-view-360"
                  title="Open this customer's 360° view">
                  <Users className="w-3.5 h-3.5" /> View 360
                </button>
              )}
              <button
                onClick={() => setPaletteOpen(true)}
                className="t2-btn-ghost t2-btn-full"
                style={{ fontSize: 10.5 }}
                data-testid="customer-change"
              >
                <Search className="w-3.5 h-3.5" /> Change
              </button>
            </div>
          </>
        ) : (
          <button
            onClick={() => setPaletteOpen(true)}
            className="t2-btn-primary t2-btn-full"
            data-testid="customer-search-btn"
          >
            <Search className="w-3.5 h-3.5" /> Search customer…
          </button>
        )}
      </T2Panel>

      <T2CommandPalette
        open={paletteOpen && !newLeadDraft}
        onClose={() => setPaletteOpen(false)}
        onQuery={query}
        toolbar={
          <>
            <div style={{ display: "inline-flex", gap: 4, background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: 3 }}>
              {[
                { k: "all", label: "All", n: counts.all },
                { k: "deals", label: "Deals", n: counts.deals },
                { k: "leads", label: "Leads", n: counts.leads },
              ].map(({ k, label, n }) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`quote-search-scope-${k}`}
                  onClick={() => changeScope(k)}
                  className="t2-mono"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "4px 10px", borderRadius: 6, cursor: "pointer",
                    fontSize: 10.5, fontWeight: 700, border: "none",
                    background: scope === k ? "var(--t2-orange)" : "transparent",
                    color: scope === k ? "#0a0e17" : "var(--t2-grey2)",
                  }}
                >
                  {label}
                  <span style={{
                    fontSize: 9, padding: "1px 5px", borderRadius: 999,
                    background: scope === k ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.08)",
                    color: scope === k ? "#0a0e17" : "var(--t2-grey3)",
                  }}>{n}</span>
                </button>
              ))}
            </div>
            <span className="t2-mono" style={{ fontSize: 9.5, color: "var(--t2-grey3)" }}>
              searches name · email · company · phone
            </span>
          </>
        }
        onSelect={(r) => { onPick(r); setPaletteOpen(false); }}
        items={results}
        placeholder="Search email, name, company, phone…"
        empty="Type at least 2 characters to search customers, or add a new lead below."
        renderActions={(r, { close }) => (
          <>
            {!r.isLead && (
            <button
              type="button"
              className="t2-row-action-btn"
              data-testid={`customer-row-open-last-${r._key}`}
              onClick={async () => {
                try {
                  const { data } = await api.get(`/quotes?scope=all&search=${encodeURIComponent(r.email || "")}&limit=1&sort=updated`);
                  const q = (data.items || [])[0];
                  if (q?._id) { close(); navigate(`/quotes/${q._id}/edit`); }
                  else toast.info(`No quotes yet for ${r.email}. Pick the customer to start one.`);
                } catch { toast.error("Couldn't check last quote"); }
              }}
              title="Open the most-recent quote for this customer"
            >Open last</button>
            )}
            <button
              type="button"
              className="t2-row-action-btn"
              data-testid={`customer-row-view-quotes-${r._key}`}
              onClick={() => {
                setPaletteOpen(false);   // merge — close palette so peek doesn't fight z-index
                setQuotesPeek({ email: r.email, name: [r.firstName, r.lastName].filter(Boolean).join(" ") || r.email });
              }}
              title="Peek at all quotes for this customer"
            >View quotes</button>
          </>
        )}
        // `emptyAction` is invoked as a function by T2CommandPalette
        // (not rendered as <Component />), so this arrow doesn't create a
        // new component type per render — the returned JSX is a *reference*
        // to the hoisted `EmptyStateCreateLead`. Lint false positive.
        // eslint-disable-next-line react/no-unstable-nested-components
        emptyAction={({ query: q }) => (
          <EmptyStateCreateLead query={q} onSeed={setNewLeadDraft} />
        )}
        renderRow={(r) => (
          <>
            <div>
              {r.isLead
                ? <T2Chip tone="sky"><span data-testid="quote-result-type-chip">LEAD</span></T2Chip>
                : (r.tier ? <T2TierChip tier={r.tier} /> : <T2Chip><span data-testid="quote-result-type-chip">DEAL</span></T2Chip>)
              }
              <div className="t2-mono" style={{ fontSize: 9.5, color: "var(--t2-grey3)", marginTop: 4 }}>
                {r.isLead ? "prospect" : `${r.orders || 0} ords`}
              </div>
            </div>
            <div>
              <div className="t2-cmd-name">{[r.firstName, r.lastName].filter(Boolean).join(" ") || r.email}</div>
              <div className="t2-cmd-meta">
                <span style={{ color: "var(--t2-orange-ink)" }}>{r.email}</span>
                {r.company && <span> · {r.company}</span>}
                {r.phone && <span> · {r.phone}</span>}
              </div>
            </div>
            <div className="t2-cmd-price">
              {r.isLead
                ? <><b style={{ color: "var(--t2-grey3)" }}>£0.00</b><small>Prospect</small></>
                : <><b>{GBP(r.ltv)}</b><small>Lifetime</small></>
              }
            </div>
          </>
        )}
      />

      <NewLeadInlineModal
        open={!!newLeadDraft}
        seed={newLeadDraft?.seed || ""}
        onClose={() => { setNewLeadDraft(null); setPaletteOpen(false); }}
        onCreate={attachLead}
      />

      <QuotesPeekModal
        open={!!quotesPeek}
        email={quotesPeek?.email}
        name={quotesPeek?.name}
        onClose={() => setQuotesPeek(null)}
        onOpen={(id) => { setQuotesPeek(null); setPaletteOpen(false); navigate(`/quotes/${id}/edit`); }}
      />
    </>
  );
}
